'use client'

import { useEffect, useRef } from 'react'
import { trackLibrarySearch } from '@/lib/tracking'

export const LIBRARY_SEARCH_SETTLE_MS = 800

interface LibrarySearchTrackingInput {
  query: string
  resultCount: number
  /** False while the list for this query is still fetching or revalidating. */
  settled: boolean
  category?: string
  model?: string
  /** The results shown came from a typo correction or a loosened query. */
  rescued?: boolean
  source: string
}

/**
 * Fires `library_search` once per settled query: the query text and its
 * filters must hold still for LIBRARY_SEARCH_SETTLE_MS, and the same
 * query/category/model combination is never reported twice in a row. Clearing
 * the box and retyping the same query is not a new search, so it is not
 * reported again either; only a different query, category or model is.
 */
export function useLibrarySearchTracking({
  query,
  resultCount,
  settled,
  category,
  model,
  rescued,
  source,
}: LibrarySearchTrackingInput) {
  const lastReportedRef = useRef<string | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed || !settled) return
    const key = JSON.stringify([trimmed.toLowerCase(), category ?? '', model ?? ''])
    if (lastReportedRef.current === key) return

    const timer = setTimeout(() => {
      lastReportedRef.current = key
      trackLibrarySearch({ query: trimmed, result_count: resultCount, category, model, rescued, source })
    }, LIBRARY_SEARCH_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [query, resultCount, settled, category, model, rescued, source])
}
